/*
 * hover-tooltip — the sparkline crosshair readout's DOM half (the value dots +
 * the value/time chip), extracted so the History timeline shows the IDENTICAL
 * readout a hover on a grid sparkline shows. Reuses the grid's CSS classes
 * (.sg-sparkline-dot / .sg-sparkline-tooltip, styled in symbolGrid.scss);
 * elements are appended to document.body (position: fixed) so they escape any
 * cell/panel overflow, exactly as SymbolGrid does. One instance per host
 * component; dispose() on disconnect.
 *
 * MULTI-ROW: the timeline stacks up to three lanes, so a readout is one dot
 * PER series plus ONE chip with a row per series and a single time line —
 * three separate cursor-anchored chips would land on top of each other. With
 * one row the chip renders byte-identically to the original single-series
 * markup (value span + time span), so the grid sparkline hover and a
 * single-lane timeline hover still look the same.
 *
 * Dot and row elements are POOLED and updated via style/textContent only —
 * show() runs on every pointermove, so per-event createElement would thrash
 * the DOM exactly where smoothness matters most.
 *
 * SymbolGrid's own inlined copy is deliberately left untouched this pass —
 * it's a tuned hot path; re-pointing it here is a follow-up, not a requirement.
 */

export interface HoverReadoutRow {
  /** Where this series' DOT sits — the data point, in viewport (client) px.
   *  Omitted when the host draws its own marker (the full chart's SVG crosshair dot). */
  dotClientX?: number;
  dotClientY?: number;
  /** Series color (the dot's ring + the value text). */
  color: string;
  valueText: string;
}

export interface HoverReadout {
  /** Where the CHIP hangs — the cursor, in viewport (client) px. */
  cursorClientX: number;
  cursorClientY: number;
  timeText: string;
  /** One entry per series with a value at this instant (1..n). */
  rows: HoverReadoutRow[];
}

export class ChartHoverTooltip {
  private dots: HTMLDivElement[] = [];
  private tip: HTMLDivElement | null = null;
  private rowEls: HTMLSpanElement[] = [];
  private valueEls: HTMLSpanElement[] = [];
  private timeEl: HTMLSpanElement | null = null;

  show(r: HoverReadout): void {
    if (!this.tip) {
      this.tip = document.createElement('div');
      this.tip.className = 'vg-sparkline-tooltip';
      this.timeEl = document.createElement('span');
      this.timeEl.className = 'vg-tooltip-time sg-tooltip-time';
      this.tip.appendChild(this.timeEl);
      document.body.appendChild(this.tip);
    }
    // Grow the pools to fit (never shrink — surplus hides below).
    while (this.dots.length < r.rows.length) {
      const dot = document.createElement('div');
      dot.className = 'vg-sparkline-dot';
      document.body.appendChild(dot);
      this.dots.push(dot);
    }
    while (this.rowEls.length < r.rows.length) {
      const row = document.createElement('span');
      row.className = 'sg-tooltip-row';
      const value = document.createElement('span');
      value.className = 'vg-tooltip-value';
      row.appendChild(value);
      // Rows stack above the single time line, in lane order.
      this.tip.insertBefore(row, this.timeEl);
      this.rowEls.push(row);
      this.valueEls.push(value);
    }
    for (let i = 0; i < this.dots.length; i++) {
      const dot = this.dots[i];
      const row = r.rows[i];
      if (!row) {
        dot.style.display = 'none';
        this.rowEls[i].style.display = 'none';
        continue;
      }
      if (row.dotClientX === undefined || row.dotClientY === undefined) {
        dot.style.display = 'none';
      } else {
        dot.style.display = '';
        dot.style.left = `${row.dotClientX}px`;
        dot.style.top = `${row.dotClientY}px`;
        dot.style.borderColor = row.color;
      }
      this.rowEls[i].style.display = '';
      this.valueEls[i].style.color = row.color;
      this.valueEls[i].textContent = row.valueText;
    }
    this.tip.style.display = '';
    // Single row keeps the original inline value+time chip; the modifier only
    // stacks when there is genuinely more than one series to read out.
    this.tip.classList.toggle('sg-tooltip-multi', r.rows.length > 1);
    this.tip.style.left = `${r.cursorClientX + 12}px`;
    this.tip.style.top = `${r.cursorClientY - 30}px`;
    this.timeEl!.textContent = r.timeText;
  }

  hide(): void {
    for (const dot of this.dots) dot.style.display = 'none';
    if (this.tip) this.tip.style.display = 'none';
  }

  dispose(): void {
    for (const dot of this.dots) dot.remove();
    this.tip?.remove();
    this.dots = [];
    this.rowEls = [];
    this.valueEls = [];
    this.tip = null;
    this.timeEl = null;
  }
}
